import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProjects } from "@/hooks/useProjects";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger } from
"@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, FlaskConical, Clock, MoreHorizontal, Pencil, CalendarIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger } from
"@/components/ui/dropdown-menu";
import { format } from "date-fns";
import { AppHeader } from "@/components/AppHeader";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

const statusColors: Record<string, string> = {
  planning: "bg-accent text-accent-foreground",
  "in-progress": "bg-primary/15 text-primary",
  complete: "bg-emerald-100 text-emerald-700"
};

const ProjectsList = () => {
  const { projects, createProject, updateProject, deleteProject } = useProjects();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [target, setTarget] = useState("");
  const [deadline, setDeadline] = useState<Date | undefined>(undefined);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);

  const handleCreate = () => {
    if (!name.trim()) return;
    if (editingProjectId) {
      updateProject(editingProjectId, {
        name: name.trim(),
        description: description.trim(),
        targetCompound: target.trim(),
        deadline: deadline ? deadline.toISOString().split("T")[0] : undefined
      });
      resetDialog();
    } else {
      const project = createProject(name.trim(), target.trim(), description.trim(), deadline ? deadline.toISOString().split("T")[0] : undefined);
      resetDialog();
      navigate(`/project/${project.id}`);
    }
  };

  const resetDialog = () => {
    setName("");
    setDescription("");
    setTarget("");
    setDeadline(undefined);
    setEditingProjectId(null);
    setOpen(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      resetDialog();
    } else {
      setOpen(true);
    }
  };

  const handleEdit = (projectId: string) => {
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;
    setEditingProjectId(projectId);
    setName(project.name);
    setDescription(project.description || "");
    setTarget(project.targetCompound || "");
    setDeadline(project.deadline ? new Date(project.deadline) : undefined);
    
    setOpen(true);
  };

  const isEditing = !!editingProjectId;

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-5xl px-6 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Synthesis Plans </h1>
            <p className="text-sm text-muted-foreground mt-1">Manage your synthesis routes

            </p>
          </div>
          <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" /> New Plan
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>
                  {isEditing ? "Edit Plan" : "Create New Plan"}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 pt-2">
                  <div>
                    <Label>Name</Label>
                    <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Ibuprofen"
                    className="mt-1.5" />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="What is this synthesis for?"
                    className="mt-1.5"
                    rows={3} />
                  </div>
                  <Button
                  onClick={handleCreate}
                  disabled={!name.trim()}
                  className="w-full mt-2">
                    {isEditing ? "Save Changes" : "Start Planning"}
                  </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {projects.length === 0 ?
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16">
            <FlaskConical className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground font-medium">No compounds yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Create your first compound synthesis route
            </p>
          </div> :

        <div className="space-y-3">
            {projects.map((project) =>
          <Card
            key={project.id}
            className="cursor-pointer transition-all hover:border-primary/30"
            onClick={() => navigate(`/project/${project.id}`)}>
            
                <CardContent className="flex items-center justify-between p-5">
                  <div className="flex items-center gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                      <FlaskConical className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{project.name}</h3>
                      {project.description &&
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1 max-w-md">
                          {project.description}
                        </p>
                  }
                      <div className="flex items-center gap-3 mt-1">
                        {project.targetCompound &&
                    <span className="text-xs text-muted-foreground">
                            Target: {project.targetCompound}
                          </span>
                    }
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {format(new Date(project.lastModified), "MMM d, yyyy")}
                        </span>
                        {project.deadline &&
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <CalendarIcon className="h-3 w-3" />
                            Due {format(new Date(project.deadline), "MMM d, yyyy")}
                          </span>
                    }
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">


                    <span className="text-xs text-muted-foreground">
                      {(() => {
                        const rootProducts = new Set<string>();
                        project.steps.filter(s => !s.parentId).forEach(s =>
                          s.products.forEach(p => {
                            const key = p.casNumber || p.name || p.formula || p.id;
                            if (key) rootProducts.add(key);
                          })
                        );
                        const count = rootProducts.size;
                        return count > 0 ? `${count} compound${count !== 1 ? "s" : ""} · ` : "";
                      })()}
                      {project.steps.length} step{project.steps.length !== 1 ? "s" : ""}
                    </span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem onClick={() => handleEdit(project.id)}>
                          <Pencil className="mr-2 h-4 w-4" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => deleteProject(project.id)}>
                      
                          <Trash2 className="mr-2 h-4 w-4" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
          )}
          </div>
        }
      </main>
    </div>);

};

export default ProjectsList;